import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const Landing = lazy(() => import("@/pages/Landing"));
const GuidesIndex = lazy(() => import("@/pages/guides/GuidesIndex"));
const GuidePage = lazy(() => import("@/pages/guides/GuidePage"));
const Auth = lazy(() => import("@/pages/Auth"));
const IQAssistantPage = lazy(() => import("@/pages/IQAssistantPage"));
const IQAssistantSetup = lazy(() => import("@/pages/IQAssistantSetup"));
const IQAssistantInstall = lazy(() => import("@/pages/IQAssistantInstall"));
const IQAssistantInvite = lazy(() => import("@/pages/IQAssistantInvite"));
const DesktopAssistantPage = lazy(() => import("@/pages/DesktopAssistantPage"));
const DesktopAssistantV2 = lazy(() => import("@/pages/DesktopAssistantV2"));
const DesktopAssistantSettingsPage = lazy(() => import("@/pages/settings/DesktopAssistantSettingsPage"));
const ConnectionsPage = lazy(() => import("@/pages/settings/ConnectionsPage"));

const BriefingSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/BriefingSurface"));
const AskSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/AskSurface"));
const VoiceSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/VoiceSurface"));
const TeamSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/TeamSurface"));
const DashboardSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/DashboardSurface"));
const FinanceSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/FinanceSurface"));
const ApprovalsSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/ApprovalsSurface"));
const MeetingsSurface = lazy(() => import("@/components/desktop-assistant/v2/surfaces/MeetingsSurface"));
const OvernightDigestPage = lazy(() => import("@/components/desktop-assistant/v2/overnight/OvernightDigestPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <SubscriptionProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/login" element={<Navigate to="/auth" replace />} />

                  <Route path="/iq-assistant" element={<IQAssistantPage />} />
                  <Route path="/iq-assistant/setup" element={<IQAssistantSetup />} />
                  <Route path="/iq-assistant/install" element={<IQAssistantInstall />} />
                  <Route path="/iq-assistant/invite" element={<IQAssistantInvite />} />
                  <Route path="/iq-assistant/ops" element={<Navigate to="/desktop-assistant/v2" replace />} />

                  <Route path="/desktop-assistant" element={<ProtectedRoute><DesktopAssistantPage /></ProtectedRoute>} />
                  <Route path="/desktop-assistant/app/*" element={<Navigate to="/desktop-assistant/v2" replace />} />
                  <Route path="/desktop-assistant/v2" element={<ProtectedRoute><DesktopAssistantV2 /></ProtectedRoute>}>
                    <Route index element={<Navigate to="briefing" replace />} />
                    <Route path="briefing" element={<BriefingSurface />} />
                    <Route path="ask" element={<AskSurface />} />
                    <Route path="voice" element={<VoiceSurface />} />
                    <Route path="team" element={<TeamSurface />} />
                    <Route path="dashboard" element={<DashboardSurface />} />
                    <Route path="finance" element={<FinanceSurface />} />
                    <Route path="approvals" element={<ApprovalsSurface />} />
                    <Route path="meetings" element={<MeetingsSurface />} />
                    <Route path="overnight" element={<OvernightDigestPage />} />
                  </Route>

                  <Route path="/settings/desktop-assistant" element={<ProtectedRoute><DesktopAssistantSettingsPage /></ProtectedRoute>} />
                  <Route path="/settings/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />

                  <Route path="/" element={<Landing />} />
                  <Route path="/guides" element={<GuidesIndex />} />
                  <Route path="/guides/:slug" element={<GuidePage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </SubscriptionProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
